import React, { useState } from 'react';
import { Box, ActionIcon, Group, Popover } from '@mantine/core';
import { Bold, Italic, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered, Code, Quote, Minus, Undo, Redo, Link as LinkIcon, Unlink, Smile } from 'lucide-react';
import { useNoteEditor } from '../../contexts/NoteEditorContext';

interface CompactRichTextToolbarProps {
  editor: any;
}

const CompactRichTextToolbar: React.FC<CompactRichTextToolbarProps> = ({ editor }) => {
  if (!editor) return null;

  const iconSize = 16;
  const buttonSize = 'sm';
  const [emojiOpened, setEmojiOpened] = useState(false);

  const insertEmoji = (emoji: string) => {
    editor.chain().focus().insertContent(emoji).run();
    setEmojiOpened(false);
  };

  const setOrToggleLink = () => {
    // Extend current link mark range and prompt for URL
    const prev = editor.getAttributes('link')?.href as string | undefined;
    const url = window.prompt('Enter URL', prev || 'https://');
    if (url === null) return; // cancelled
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
  const { from, empty } = editor.state.selection;
    if (empty) {
      // Insert the URL text and apply link to it
      editor
        .chain()
        .focus()
        .insertContent(url)
        .setTextSelection({ from, to: from + url.length })
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  return (
    <Group gap={4} wrap="nowrap">
      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('bold') ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleBold().run();
        }}
        title="Bold"
      >
        <Bold size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('italic') ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleItalic().run();
        }}
        title="Italic"
      >
        <Italic size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('strike') ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleStrike().run();
        }}
        title="Strikethrough"
      >
        <Strikethrough size={iconSize} />
      </ActionIcon>

      <Box style={{ width: '1px', height: '20px', backgroundColor: 'var(--app-border)' }} />

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('heading', { level: 1 }) ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleHeading({ level: 1 }).run();
        }}
        title="Heading 1"
      >
        <Heading1 size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('heading', { level: 2 }) ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleHeading({ level: 2 }).run();
        }}
        title="Heading 2"
      >
        <Heading2 size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('heading', { level: 3 }) ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleHeading({ level: 3 }).run();
        }}
        title="Heading 3"
      >
        <Heading3 size={iconSize} />
      </ActionIcon>

      <Box style={{ width: '1px', height: '20px', backgroundColor: 'var(--app-border)' }} />

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('bulletList') ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleBulletList().run();
        }}
        title="Bullet List"
      >
        <List size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('orderedList') ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleOrderedList().run();
        }}
        title="Numbered List"
      >
        <ListOrdered size={iconSize} />
      </ActionIcon>

      <Box style={{ width: '1px', height: '20px', backgroundColor: 'var(--app-border)' }} />

      {/* Link / Unlink */}
      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('link') ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          setOrToggleLink();
        }}
        title={editor.isActive('link') ? 'Edit Link' : 'Add Link'}
      >
        <LinkIcon size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant="subtle"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().focus().unsetLink().run();
        }}
        disabled={!editor.isActive('link')}
        title="Remove Link"
      >
        <Unlink size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('code') ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleCode().run();
        }}
        title="Inline Code"
      >
        <Code size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant={editor.isActive('blockquote') ? 'filled' : 'subtle'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().toggleBlockquote().run();
        }}
        title="Quote"
      >
        <Quote size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant="subtle"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().setHorizontalRule().run();
        }}
        title="Horizontal Line"
      >
        <Minus size={iconSize} />
      </ActionIcon>

      {/* Emoji picker */}
      <Popover opened={emojiOpened} onChange={setEmojiOpened} position="bottom" withArrow shadow="md">
        <Popover.Target>
          <ActionIcon
            size={buttonSize}
            variant="subtle"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              setEmojiOpened((o) => !o);
            }}
            title="Insert Emoji"
          >
            <Smile size={iconSize} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Box style={{ maxWidth: 240 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 6,
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              {['😀','😅','😊','😍','🤔','👍','✅','🔥','⚠️','📌','📝','💡','📎','🗓️','⭐','❗','❓','🚀','🥳','🙌','👏','🙏','💯','🤝','🧠','💬','📣','🧩','⏱️','🕒'].map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => insertEmoji(e)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--app-border)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    padding: 4,
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          </Box>
        </Popover.Dropdown>
      </Popover>

      <Box style={{ width: '1px', height: '20px', backgroundColor: 'var(--app-border)' }} />

      <ActionIcon
        size={buttonSize}
        variant="subtle"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().undo().run();
        }}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo size={iconSize} />
      </ActionIcon>

      <ActionIcon
        size={buttonSize}
        variant="subtle"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          editor.chain().redo().run();
        }}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo size={iconSize} />
      </ActionIcon>
    </Group>
  );
};

export const FloatingRichTextToolbar: React.FC = () => {
  const context = useNoteEditor();
  
  // Always show the toolbar in organized/free modes
  return (
    <Box
      style={{
        borderBottom: '1px solid var(--app-border)',
        backgroundColor: 'var(--app-surface)',
        padding: '6px 16px',
        display: 'flex',
        justifyContent: 'center',
        minHeight: '38px'
      }}
    >
      {context?.activeEditor ? (
        <CompactRichTextToolbar editor={context.activeEditor} />
      ) : (
        <Group gap={4} wrap="nowrap" style={{ opacity: 0.5 }}>
          <ActionIcon size="sm" variant="subtle" disabled>
            <Bold size={16} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" disabled>
            <Italic size={16} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" disabled>
            <Strikethrough size={16} />
          </ActionIcon>
          
          <Box style={{ width: '1px', height: '20px', backgroundColor: 'var(--app-border)' }} />
          
          <ActionIcon size="sm" variant="subtle" disabled>
            <Heading1 size={16} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" disabled>
            <Heading2 size={16} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" disabled>
            <Heading3 size={16} />
          </ActionIcon>
          
          <Box style={{ width: '1px', height: '20px', backgroundColor: 'var(--app-border)' }} />
          
          <ActionIcon size="sm" variant="subtle" disabled>
            <List size={16} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" disabled>
            <ListOrdered size={16} />
          </ActionIcon>
          
          <Box style={{ width: '1px', height: '20px', backgroundColor: 'var(--app-border)' }} />
          
          <ActionIcon size="sm" variant="subtle" disabled>
            <Code size={16} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" disabled>
            <Quote size={16} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" disabled>
            <Minus size={16} />
          </ActionIcon>
          
          <Box style={{ width: '1px', height: '20px', backgroundColor: 'var(--app-border)' }} />
          
          <ActionIcon size="sm" variant="subtle" disabled>
            <Undo size={16} />
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" disabled>
            <Redo size={16} />
          </ActionIcon>
        </Group>
      )}
    </Box>
  );
};